import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Inbox, MessageSquare, Volume2, VolumeX } from "lucide-react";

const SOUND_PREF_KEY = "pyth.reviewerSoundEnabled";

/**
 * Play a discreet two-tone "ding" using Web Audio API. No external assets
 * needed. Volume is intentionally soft (gain 0.15) so it doesn't startle
 * a reviewer working on multiple windows.
 */
function playDing() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const tones = [
      { freq: 880, start: 0,    dur: 0.16 },
      { freq: 1320, start: 0.18, dur: 0.22 },
    ];
    for (const t of tones) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = t.freq;
      gain.gain.setValueAtTime(0.0001, now + t.start);
      gain.gain.exponentialRampToValueAtTime(0.15, now + t.start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + t.start + t.dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + t.start);
      osc.stop(now + t.start + t.dur + 0.05);
    }
    // Free up the context once playback is done
    setTimeout(() => { try { ctx.close(); } catch { /* noop */ } }, 600);
  } catch {
    /* Audio unavailable — silently ignore */
  }
}

/**
 * ReviewerHeaderBadge — bandeau notification temps-réel pour Reviewers
 * Fortinet (et admins). Affiche dans le Header :
 *   - le nombre de revues en attente (review_status = requested)
 *   - le nombre de revues avec une réponse partenaire non lue
 *     (last_message_role = partner ET review_status = requested)
 *
 * Polling 30 s. Lien direct vers /reviews. Caché pour les non-reviewers.
 *
 * Mode son : un "ding" discret (Web Audio) joué UNE fois quand le compteur
 * `unreplied` augmente. Toggle persistant dans localStorage. Le tout
 * premier sample (initial load) ne joue jamais le son.
 */
export default function ReviewerHeaderBadge() {
  const { user } = useAuth();
  const [pending, setPending] = useState(0);
  const [unreplied, setUnreplied] = useState(0);
  const [soundOn, setSoundOn] = useState(() => {
    try { return localStorage.getItem(SOUND_PREF_KEY) === "1"; } catch { return false; }
  });
  const lastUnrepliedRef = useRef(null);
  const soundOnRef = useRef(soundOn);
  useEffect(() => { soundOnRef.current = soundOn; }, [soundOn]);

  const allowed = !!(user?.is_admin || user?.is_fortinet_reviewer);

  const refresh = useCallback(async () => {
    if (!allowed) return;
    try {
      const r = await api.get("/reviews/queue");
      const rows = r.data || [];
      const requested = rows.filter((q) => q.review_status === "requested");
      const partnerWaiting = requested.filter((q) => q.last_message_role === "partner");
      setPending(requested.length);
      const newUnreplied = partnerWaiting.length;
      const prev = lastUnrepliedRef.current;
      // Play the ding only when the count strictly increases AND we already
      // had a baseline (skip the very first poll so the user doesn't get a
      // spurious chime just for opening the app).
      if (prev !== null && newUnreplied > prev && soundOnRef.current) {
        playDing();
      }
      lastUnrepliedRef.current = newUnreplied;
      setUnreplied(newUnreplied);
    } catch {
      // silent — Header is ambient UI, no toast for poll failures
    }
  }, [allowed]);

  useEffect(() => {
    if (!allowed) {
      setPending(0);
      setUnreplied(0);
      lastUnrepliedRef.current = null;
      return;
    }
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [allowed, refresh]);

  const toggleSound = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSoundOn((prev) => {
      const next = !prev;
      try { localStorage.setItem(SOUND_PREF_KEY, next ? "1" : "0"); } catch { /* noop */ }
      // Play a confirmation ding immediately when turning ON so the user
      // knows the setting + audio context work.
      if (next) playDing();
      return next;
    });
  };

  if (!allowed) return null;

  // Quiet state — no pending revues: render nothing to keep the header clean
  if (pending === 0 && unreplied === 0) return null;

  const tooltip = unreplied > 0
    ? `${unreplied} revue${unreplied > 1 ? "s" : ""} en attente de votre réponse`
    : `${pending} revue${pending > 1 ? "s" : ""} en attente`;

  return (
    <div className="inline-flex items-center mr-1" data-testid="reviewer-header-badge-root">
      <Link
        to="/reviews"
        title={tooltip}
        data-testid="reviewer-header-badge"
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-l-sm border border-[#D42B1E]/30 bg-[#D42B1E]/5 hover:bg-[#D42B1E]/10 transition-colors text-[11px] font-mono font-semibold text-[#991D14]"
      >
        <span className="relative inline-flex">
          <Inbox className="h-3.5 w-3.5" />
          {unreplied > 0 && (
            <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-[#D42B1E] animate-pulse" />
          )}
        </span>
        <span data-testid="reviewer-header-pending-count">{pending}</span>
        <span className="hidden md:inline opacity-70">revue{pending > 1 ? "s" : ""}</span>
        {unreplied > 0 && (
          <span
            className="hidden md:inline-flex items-center gap-0.5 ml-1 px-1.5 py-0.5 rounded-sm bg-[#D42B1E] text-white text-[10px]"
            data-testid="reviewer-header-unreplied-count"
          >
            <MessageSquare className="h-2.5 w-2.5" />
            {unreplied}
          </span>
        )}
      </Link>
      <button
        type="button"
        onClick={toggleSound}
        aria-pressed={soundOn}
        aria-label={soundOn ? "Désactiver les notifications sonores" : "Activer les notifications sonores"}
        title={soundOn ? "Notifications sonores activées — cliquer pour couper" : "Notifications sonores coupées — cliquer pour activer"}
        data-testid="reviewer-header-sound-toggle"
        className={`px-1.5 py-1 rounded-r-sm border border-l-0 transition-colors ${
          soundOn
            ? "bg-[#D42B1E] text-white border-[#D42B1E] hover:bg-[#B32218]"
            : "bg-white text-zinc-500 border-[#D42B1E]/30 hover:text-[#D42B1E] hover:border-[#D42B1E]/50"
        }`}
      >
        {soundOn ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}
